<script setup lang="ts">
interface Props {
  collections: string[];
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Search...',
});

// Validate collections prop
if (!Array.isArray(props.collections) || props.collections.length === 0) {
  throw new Error('GlobalSearch: collections prop must be a non-empty array');
}

function truncate(str: string, n: number) {
  return str.length > n ? str.substr(0, n - 1) + '...' : str;
}

defineShortcuts({
  meta_k: {
    usingInput: true,
    handler: () => {
      isOpen.value = !isOpen.value;
    },
  },
});

const isOpen = ref(false);
const query = ref('');
const results = ref<any[]>([]);
const loading = ref(false);
const searchInput = ref<HTMLInputElement>();

const { fileUrl } = useFiles();

// Search ranking algorithm with recency boost
function calculateSearchScore(result: any, searchQuery: string): number {
  let score = 0;

  const searchTerms = searchQuery
    .toLowerCase()
    .split(' ')
    .filter((term) => term.length > 0);

  const title = (result.title || '').toLowerCase();
  const description = (result.description || '').toLowerCase();
  const content = (result.content || '').toLowerCase();

  // Process each search term individually for relevance scoring
  for (const term of searchTerms) {
    // Priority 1: Title starts with search term (1000 points per term)
    if (title.startsWith(term)) {
      score += 1000;
    }
    // Priority 2: Title contains search term anywhere (500 points per term)
    else if (title.includes(term)) {
      score += 500;
    }

    // Priority 3: Description/summary contains search term (100 points per term)
    if (description.includes(term)) {
      score += 100;
    }

    // Priority 4: Content contains search term (10 points per term)
    if (content.includes(term)) {
      score += 10;
    }
  }

  // Add recency boost (up to 50 points based on publication date)
  // This enhances relevance without overpowering it
  if (result.date_published) {
    try {
      const publicationDate = new Date(result.date_published);

      // Check if the date is valid
      if (isNaN(publicationDate.getTime())) {
        // Invalid date, skip recency boost
        return score;
      }

      const now = new Date();
      const daysSincePublished = (now.getTime() - publicationDate.getTime()) / (1000 * 60 * 60 * 24);

      // Recency boost calculation:
      // - Articles from today: +50 points
      // - Articles from 1 month ago: +35 points
      // - Articles from 6 months ago: +15 points
      // - Articles from 1+ years ago: +0 points
      const maxBoost = 50;
      const decayDays = 365; // Full decay over 1 year
      const recencyBoost = Math.max(0, maxBoost * (1 - daysSincePublished / decayDays));

      score += Math.round(recencyBoost);
    } catch (error) {
      // If date parsing fails, no recency boost applied
    }
  }

  return score;
}

// Debounced search function
const searchToken = ref(0);
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const debouncedSearch = useDebounceFn(async (q: string) => {
  if (!q || q.length < 3) {
    results.value = [];
    loading.value = false;
    return;
  }

  const token = ++searchToken.value;
  loading.value = true;

  // Small delay to avoid spamming the API
  await delay(250);
  if (token !== searchToken.value) return;

  try {
    const response = await $fetch('/api/search', {
      params: {
        search: q,
        collections: props.collections,
      },
    });

    // Only update results if this is still the current search
    if (token === searchToken.value) {
      const mappedResults = (response?.data || []).map((hit: any) => ({
        id: hit.id,
        title: hit.title,
        url: hit.url,
        image: hit.image,
        description: hit.description,
        content: hit.content,
        date_published: hit.date_published,
        date_updated: hit.date_updated,
        isInternal: typeof hit?.url === 'string' && hit.url.startsWith('/'),
        searchScore: calculateSearchScore(hit, q), // Calculate score with recency boost
      }));

      // Sort by search score (highest first) - now includes recency boost
      results.value = mappedResults.sort((a, b) => b.searchScore - a.searchScore);
    }
  } catch (error) {
    console.error('Search error:', error);

    if (token === searchToken.value) {
      results.value = [];
    }
  } finally {
    if (token === searchToken.value) {
      loading.value = false;
    }
  }
}, 300);

// Watch query changes
watch(query, (newQuery) => {
  debouncedSearch(newQuery);
});

// Handle item click
function handleResultClick(result: any) {
  if (!result.url) return;

  // Close modal first
  closeModal();

  // Navigate based on link type
  nextTick(() => {
    if (result.isInternal) {
      navigateTo(result.url);
    } else {
      window.open(result.url, '_blank');
    }
  });
}

// Close modal and reset state
function closeModal() {
  isOpen.value = false;
  query.value = '';
  results.value = [];
  loading.value = false;
}

// Focus input when modal opens
watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      searchInput.value?.focus();
    });
  } else {
    // Reset everything when modal closes
    query.value = '';
    results.value = [];
    loading.value = false;
  }
});
</script>

<template>
  <div>
    <UButton icon="material-symbols:search-rounded" color="neutral" size="lg" @click="isOpen = true">
      Search
      <div class="flex items-center gap-0.5">
        <UKbd>⌘</UKbd>
        <UKbd>K</UKbd>
      </div>
    </UButton>

    <UModal v-model:open="isOpen">
      <template #content>
        <div class="p-0">
          <!-- Search Header -->
          <div class="flex items-center border-b border-totem-pole-500 dark:border-totem-pole-500 px-4 py-3">
            <UIcon name="material-symbols:search-rounded" class="h-5 w-5 text-gray-400 mr-3" />
            <input
              ref="searchInput"
              v-model="query"
              :placeholder="props.placeholder"
              class="flex-1 border-0 bg-transparent text-black dark:text-white placeholder-gray-800 dark:placeholder-gray-300 focus:ring-0 focus:outline-none"
              @keydown.escape="closeModal"
            >
            <UButton
              v-if="query"
              icon="i-heroicons-x-mark-20-solid"
              size="xs"
              color="neutral"
              variant="ghost"
              @click="closeModal()"
            />
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center py-8">
            <UIcon name="i-heroicons-arrow-path-20-solid" class="h-5 w-5 animate-spin text-gray-800 dark:text-gray-400" />
            <span class="ml-2 text-sm text-black dark:text-white">Searching...</span>
          </div>

          <!-- Results -->
          <div v-else-if="results.length > 0" class="max-h-96 overflow-y-auto px-4">
            <UButton
              v-for="result in results"
              :key="result.id || result.url"
              :label="truncate(result.title, 50)"
              color="primary"
              block
              class="my-2 flex justify-start dark:text-white text-white"
              @click="handleResultClick(result)"
            >
              <template #leading>
                <NuxtImg
                  :src="safeRelationId(result.image) as string"
                  :alt="result.title"
                  width="40"
                  class="w-8 h-8 rounded object-cover"
                />
              </template>
            </UButton>
          </div>

          <!-- Empty State -->
          <div v-else-if="query && query.length >= 3 && !loading" class="text-center py-8">
            <UIcon
              name="i-heroicons-magnifying-glass-20-solid"
              class="h-12 w-12 mx-auto text-black dark:text-white mb-4"
            />
            <div class="text-sm text-black dark:text-white">No results found for "{{ query }}"</div>
            <div class="text-xs text-black dark:text-white mt-1">Try adjusting your search terms</div>
          </div>

          <!-- Initial State -->
          <div v-else-if="!query || query.length < 3" class="text-center py-8">
            <UIcon name="i-heroicons-magnifying-glass-20-solid" class="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <div class="text-sm text-black dark:text-white">Start typing to search...</div>
            <div class="text-xs text-black dark:text-white mt-1">Enter at least 3 characters</div>
          </div>
        </div>
      </template>
    </UModal>
  </div>
</template>
